import api from './api';

/**
 * Broker KYC (#331). Sensitive values (PAN, date of birth, account number) go
 * IN through the write-only fields and come back only as `*_masked`; the API
 * never returns them in full to this client.
 */

export type KycStatus = 'incomplete' | 'submitted' | 'verified' | 'changes_requested';

export interface KycCheck {
  code: string;
  ok: boolean;
  message: string;
}

export interface KycSummary {
  broker: string;
  broker_name: string;
  kyc_status: KycStatus;
  kyc_submitted_at: string | null;
  kyc_verified_at: string | null;
  completeness: { done: number; total: number; percent: number };
  checks: KycCheck[];
  missing: { code: string; message: string }[];
  expiries: { kind: string; label: string; valid_until: string; state: 'none' | 'valid' | 'expiring' | 'expired' }[];
  last_decision_remarks: string;
  /** #389: the dossier's documents, derived on the server. */
  documents?: KycDocumentReviewState;
  licence?: {
    license_number: string;
    license_valid_from: string | null;
    license_valid_until: string | null;
    licence_categories: string[];
  };
}

export type KycDocumentStatus = 'pending' | 'verified' | 'rejected' | 'changes_requested' | 'superseded';

export interface KycDocumentReviewState {
  state: 'changes_requested' | 'in_review' | 'approved' | 'empty';
  counts: { pending: number; verified: number; rejected: number; changes_requested: number };
  outstanding?: { id: string; doc_type: string; status: KycDocumentStatus }[];
}

/** One row of the country admin's review queue (#389). */
export interface KycReviewRow {
  broker: string;
  broker_name: string;
  broker_code: string;
  kyc_status: KycStatus;
  kyc_submitted_at: string | null;
  kyc_verified_at: string | null;
  documents: KycDocumentReviewState;
}

export type KycDocumentDecision = 'verify' | 'reject' | 'request-changes';

export interface KycKeyPerson {
  id: string;
  role: string;
  role_display: string;
  full_name: string;
  designation: string;
  email: string;
  phone: string;
  pan_masked: string;
  has_date_of_birth: boolean;
  is_active: boolean;
}

export interface KycFinancial {
  id: string;
  fy: string;
  net_worth: string | null;
  security_deposit_amount: string | null;
}

export interface KycIndemnity {
  id: string;
  insurer_name: string;
  policy_number: string;
  sum_insured: string;
  valid_from: string;
  valid_until: string;
  is_current: boolean;
}

export interface KycBankAccount {
  id: string;
  purpose: string;
  bank_name: string;
  ifsc: string;
  account_holder: string;
  account_masked: string;
  is_primary: boolean;
  verified: boolean;
}

export interface KycDocument {
  id: string;
  doc_type: string;
  doc_type_display: string;
  name: string;
  status: KycDocumentStatus;
  status_display: string;
  remarks: string;
  valid_until: string | null;
  download_url: string | null;
  document_number_masked?: string;
  original_filename?: string;
  verified_at?: string | null;
  created_at?: string;
}

type Collection = 'key-persons' | 'financial' | 'indemnity' | 'bank-accounts' | 'documents';

const BASE = '/broker-kyc';

async function list<T>(collection: Collection, brokerId?: string): Promise<T[]> {
  const { data } = await api.get(`${BASE}/${collection}/`, brokerId ? { params: { broker: brokerId } } : undefined);
  return Array.isArray(data) ? data : (data?.results ?? []);
}

export const brokerKycService = {
  summary: async (): Promise<KycSummary> => (await api.get(`${BASE}/summary/`)).data,
  submit: async (): Promise<KycSummary> => (await api.post(`${BASE}/submit/`)).data,
  list,
  create: async <T>(collection: Collection, body: Record<string, unknown> | FormData): Promise<T> =>
    (
      await api.post(
        `${BASE}/${collection}/`,
        body,
        body instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined
      )
    ).data,
  update: async <T>(collection: Collection, id: string, body: Record<string, unknown>): Promise<T> =>
    (await api.patch(`${BASE}/${collection}/${id}/`, body)).data,
  remove: async (collection: Collection, id: string): Promise<void> => {
    await api.delete(`${BASE}/${collection}/${id}/`);
  },
};

/** The country admin's side (#389). The server scopes every call to the
 *  reviewer's country and refuses broker-side roles. */
export const kycReviewService = {
  queue: async (status?: KycStatus): Promise<KycReviewRow[]> =>
    (await api.get(`${BASE}/reviews/`, status ? { params: { status } } : undefined)).data,
  summary: async (brokerId: string): Promise<KycSummary> =>
    (await api.get(`${BASE}/summary/`, { params: { broker: brokerId } })).data,
  list: <T>(collection: Collection, brokerId: string): Promise<T[]> => list<T>(collection, brokerId),
  reviewDocument: async (id: string, decision: KycDocumentDecision, remarks: string): Promise<KycDocument> =>
    (await api.post(`${BASE}/documents/${id}/${decision}/`, { remarks })).data,
  decide: async (brokerId: string, decision: 'verified' | 'changes_requested', remarks: string): Promise<KycSummary> =>
    (await api.post(`${BASE}/${brokerId}/decision/`, { decision, remarks })).data,
  /** Pull the file through the authenticated route; the /media/ URL is never exposed. */
  download: async (document: KycDocument): Promise<void> => {
    const response = await api.get(`${BASE}/documents/${document.id}/download/`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(response.data as Blob);
    const link = window.document.createElement('a');
    link.href = url;
    link.download = document.original_filename || document.name || 'kyc-document';
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};
